'use client';

import React from 'react';
import { FocusableLink } from 'ui';
import type { MDXLinkProps } from './link';

export function MDXLink({
  href = '',
  children,
  target: _target,
  domain,
  ...props
}: MDXLinkProps & { domain: string }) {
  const localPrefixes = ['/', './', '../', `https://${domain}`];
  const isExternalLink = !localPrefixes.some((prefix) =>
    href.startsWith(prefix),
  );
  const target = _target || (isExternalLink ? '_blank' : '_self');
  const rel = isExternalLink ? 'nofollow noopener' : '';

  const hasATagChildren = hasATagChild(children);

  // If there are 'a' tag children, use div wrapper with window.open to avoid hydration errors
  if (hasATagChildren) {
    const handleClick = () => {
      if (href) {
        window.open(href, target, rel.split(' ').join(','));
      }
    };

    return (
      <div
        {...props}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        role="link"
        tabIndex={0}
        style={{ cursor: 'pointer', ...props.style }}
      >
        {children}
      </div>
    );
  }

  return (
    <FocusableLink {...props} href={href} rel={rel} target={target}>
      {children}
    </FocusableLink>
  );
}

function hasATagChild(children: React.ReactNode | undefined): boolean {
  if (!children) return false;

  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false;

    if (child.type === 'a' || child.type === MDXLink) return true;

    const props = child.props as { children: React.ReactNode | undefined };
    if (props.children) {
      return hasATagChild(props.children);
    }
    return false;
  });
}
